import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Lightbulb, X, TrendingUp, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type SuggestionType = 'info' | 'warning' | 'insight';

interface AISuggestionProps {
  type?: SuggestionType;
  title: string;
  message: string;
  details?: string;
  confidence?: number; // 0-100
  onAccept?: () => void;
  onDismiss?: () => void;
  acceptLabel?: string;
  dismissLabel?: string;
  className?: string;
}

export function AISuggestion({
  type = 'info',
  title,
  message,
  details,
  confidence,
  onAccept,
  onDismiss,
  acceptLabel = 'Apply',
  dismissLabel = 'Dismiss',
  className,
}: AISuggestionProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) return null;

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  const handleAccept = () => {
    onAccept?.();
    setIsDismissed(true);
  };

  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />;
      case 'insight':
        return <TrendingUp className="h-5 w-5" />;
      default:
        return <Lightbulb className="h-5 w-5" />;
    }
  };

  const getVariant = () => {
    switch (type) {
      case 'warning':
        return 'warning';
      case 'insight':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Alert 
      variant={getVariant()} 
      className={cn(
        'relative border-l-4',
        type === 'info' && 'border-l-blue-500 bg-blue-50/50',
        type === 'warning' && 'border-l-amber-500 bg-amber-50/50',
        type === 'insight' && 'border-l-purple-500 bg-purple-50/50',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          'mt-0.5',
          type === 'info' && 'text-blue-600',
          type === 'warning' && 'text-amber-600',
          type === 'insight' && 'text-purple-600'
        )}>
          {getIcon()}
        </div>
        
        <div className="flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <AlertTitle className="mb-1 flex items-center gap-2">
                <span className="font-semibold">{title}</span>
                {confidence !== undefined && (
                  <span className="text-xs font-normal text-muted-foreground">
                    ({confidence}% confidence)
                  </span>
                )}
              </AlertTitle>
              <AlertDescription className="text-sm">
                {message}
              </AlertDescription>
              {details && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {details}
                </p>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-transparent"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Dismiss</span>
            </Button>
          </div>

          {(onAccept || onDismiss) && (
            <div className="flex gap-2 pt-2">
              {onAccept && (
                <Button
                  size="sm"
                  onClick={handleAccept}
                  className={cn(
                    type === 'info' && 'bg-blue-600 hover:bg-blue-700',
                    type === 'warning' && 'bg-amber-600 hover:bg-amber-700',
                    type === 'insight' && 'bg-purple-600 hover:bg-purple-700'
                  )}
                >
                  {acceptLabel}
                </Button>
              )}
              {onDismiss && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDismiss}
                >
                  {dismissLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Alert>
  );
}
